import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { requestPasswordReset, formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div
        data-testid="forgot-card"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0F1117] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mb-6 font-mono uppercase tracking-widest"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to login
        </Link>
        <h1 className="font-serif text-3xl text-white font-bold mb-2">Reset your password</h1>
        <p className="text-slate-400 text-sm mb-6">
          Enter the email on your account and we'll send a reset link.
        </p>
        {sent ? (
          <div
            data-testid="forgot-sent"
            className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-sm px-4 py-4 flex items-start gap-3"
          >
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              If an account exists with that email, a reset link has been sent. Check your inbox
              (and spam). The link expires in 1 hour.
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">
                Email
              </Label>
              <div className="mt-1 relative">
                <Mail className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="forgot-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-9 bg-[#07080A] border-white/10 h-11"
                  required
                />
              </div>
            </div>
            {error && (
              <div
                data-testid="forgot-error"
                className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-4 py-3"
              >
                {error}
              </div>
            )}
            <Button
              data-testid="forgot-submit"
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
